import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Premium variants
        gold: "bg-gold text-navy font-semibold shadow-gold hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]",
        "gold-outline": "border-2 border-gold text-gold bg-transparent hover:bg-gold/10 font-semibold",
        navy: "bg-navy text-cream font-semibold hover:bg-navy-light shadow-elegant",
        "hero-primary": "bg-gold text-navy font-bold text-lg px-8 py-6 shadow-gold hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] rounded-lg",
        "hero-secondary": "border-2 border-cream/30 text-cream bg-cream/5 backdrop-blur-sm font-semibold text-lg px-8 py-6 hover:bg-cream/15 hover:border-cream/50 rounded-lg",
        cta: "bg-gold text-navy font-bold shadow-gold hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] rounded-lg",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
